import React, { useState, useEffect, useMemo, useRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import {
  Snackbar,
  Alert as MuiAlert,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Box,
  ListItemText,
  OutlinedInput,
  TextField,
  ListSubheader,
  InputAdornment,
} from "@mui/material";
import {
  IoCalendarOutline,
  IoChevronDown,
  IoPricetagsOutline,
  IoSearch,
} from "react-icons/io5";
import "./UserForm.css";
import "./SystemUserActionButtons.css";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";

const parseProjectDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [year, month, day] = normalized.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(normalized)) {
    const [day, month, year] = normalized.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const getProjectDefaultDates = (project) => ({
  start: parseProjectDate(project?.project_start ?? project?.start_date),
  end: parseProjectDate(project?.project_end ?? project?.end_date),
});

const getDurationDays = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) return null;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
};

export default function UserForm({ isSidebarExpanded }) {
  /** ---------------------- state ---------------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    adharnumber: "",
    phone_number: "",
    role: "",
    group: [],
    department_ids: [],
  });

  const [roles, setRoles] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [equipmentGroupOptions, setEquipmentGroupOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);
  const [projectAccess, setProjectAccess] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [projectError, setProjectError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showPassword, setShowPassword] = useState(false);
  const [deptSearch, setDeptSearch] = useState("");
  const [equipGroupSearch, setEquipGroupSearch] = useState({});

  // Improvement 3 — Project accordion collapse state
  const [collapsedProjects, setCollapsedProjects] = useState({});

  // Improvement 4 — Loading skeletons for dropdowns
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Improvement 5 — Unsaved changes warning
  const [isDirty, setIsDirty] = useState(false);
  const isFirstRenderRef = useRef(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectConfigRef = useRef(null);

  const navigate = useNavigate();

  /** Designation dropdown helpers (search + select all) */
  const filteredDepartments = useMemo(() => {
    const q = deptSearch.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) => d.name?.toLowerCase().includes(q));
  }, [departments, deptSearch]);

  const allFilteredSelected =
    filteredDepartments.length > 0 &&
    filteredDepartments.every((d) => formData.department_ids.includes(d.id));

  const handleDepartmentChange = (value) => {
    // "__all__" is the sentinel emitted by the Select All row.
    if (value.includes("__all__")) {
      const filteredIds = filteredDepartments.map((d) => d.id);
      setFormData((p) => ({
        ...p,
        department_ids: allFilteredSelected
          ? p.department_ids.filter((id) => !filteredIds.includes(id))
          : Array.from(new Set([...p.department_ids, ...filteredIds])),
      }));
      return;
    }
    setFormData((p) => ({ ...p, department_ids: value }));
  };

  /** ---------------------- utils ---------------------- */
  const formatDateDisplay = (d) => {
    if (!d) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const formatDateApi = (d) => {
    if (!d) return null;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const getAuthKey = () => localStorage.getItem("authKey");

  /** ---------------------- effects ---------------------- */
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_all_roles`, {
          headers: { Authorization: `Bearer ${getAuthKey()}` },
        });
        setRoles(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    };

    const fetchGroups = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get/group`, {
          headers: { Authorization: `Bearer ${getAuthKey()}` },
        });
        const groups = res?.data?.data || [];
        const mappedGroups = groups.map((g) => ({ value: g.group_id, label: g.group_name }));
        setGroupOptions(mappedGroups);
        const defaultGroupA = mappedGroups.find((g) => g.label.trim().toLowerCase() === "group a");
        if (defaultGroupA) {
          setFormData((prev) => ({ ...prev, group: [defaultGroupA.value] }));
        }
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }
    };

      const fetchProjects = async () => {
        try {
          const res = await axiosInstance.get(`${API_URL}/api/get_project`, {
            headers: { Authorization: `Bearer ${getAuthKey()}` },
          });
          const projectData = res?.data?.data || [];
          setProjects(projectData);
          const fetchedIds = Array.isArray(res?.data?.selectedProjects) ? res.data.selectedProjects : [];
          if (fetchedIds.length) setSelectedProject(fetchedIds);

          const seed = {};


          for (const p of projectData) {
            const { start, end } = getProjectDefaultDates(p);
            // Include ALL projects, regardless of date parsing success
            seed[p.project_id] = {
              project_id: p.project_id,
              project_name: p.name || "",
              start,
              end,
              equipment_group_ids: [],
            };
          }



          // Merge: preserve existing state, override with new API data
          setProjectAccess((prev) => ({ ...prev, ...seed }));
        } catch (err) {
          console.error("Failed to fetch projects:", err);
        }
      };

    const fetchEquipmentGroups = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_all_equipment_groups`, {
          headers: { Authorization: `Bearer ${getAuthKey()}` },
        });
        const options = (res?.data?.data || []).map((item) => ({
          value: item.id,
          label: `${item.equipment_group_code || ""} - ${item.equipment_group_name || ""}`.trim(),
        }));
        setEquipmentGroupOptions(options);
      } catch (err) {
        console.error("Failed to fetch equipment groups:", err);
      }
    };

    const fetchDepartments = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/departments`, {
          headers: { Authorization: `Bearer ${getAuthKey()}` },
        });
        setDepartments(res?.data?.departments || []);
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    };

    // Improvement 4 — fetch all in parallel, then clear loading state
    Promise.allSettled([fetchRoles(), fetchGroups(), fetchProjects(), fetchDepartments(), fetchEquipmentGroups()])
      .then(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  // Improvement 5 — track dirty state (skip first render)
  useEffect(() => {
    if (isFirstRenderRef.current) { isFirstRenderRef.current = false; return; }
    setIsDirty(true);
  }, [formData, selectedProject, projectAccess]);

  // Improvement 5 — beforeunload warning
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Scroll to Project Configurations when first project is selected
  useEffect(() => {
    if (selectedProject.length === 0) return;
    const t = setTimeout(() => {
      projectConfigRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedProject.length]);

  /** ---------------------- handlers ---------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Improvement 1 — inline validation on blur
  const handleBlur = (fieldName) => {
    setFieldErrors((prev) => {
      const errs = { ...prev };
      if (fieldName === "name") {
        if (!formData.name?.trim()) errs.name = "Name is required.";
        else delete errs.name;
      }
      if (fieldName === "email") {
        if (!formData.email?.trim()) errs.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errs.email = "Enter a valid email address.";
        else delete errs.email;
      }
      if (fieldName === "password") {
        if (!formData.password?.trim()) errs.password = "Password is required.";
        else delete errs.password;
      }
      if (fieldName === "phone_number") {
        if (!formData.phone_number?.trim()) errs.phone = "Phone number is required.";
        else if (!/^\d{10}$/.test(formData.phone_number.trim())) errs.phone = "Phone number must be 10 digits.";
        else delete errs.phone;
      }
      if (fieldName === "adharnumber") {
        if (formData.adharnumber?.trim() && formData.adharnumber.trim().length !== 12)
          errs.aadhar = "Aadhar number must be 12 digits.";
        else delete errs.aadhar;
      }
      return errs;
    });
  };

  // Improvement 3 — accordion toggle
  const toggleProject = (pid) => setCollapsedProjects((prev) => ({ ...prev, [pid]: !prev[pid] }));

  // Improvement 5 — cancel with dirty check
  const handleCancel = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to leave?")) return;
    }
    window.history.back();
  };

  const ensureProjectAccessKeys = (ids) => {
    setProjectAccess((prev) => {
      const next = { ...prev };
      ids.forEach((pid) => {
        const project = projects.find((p) => p.project_id === pid);
        const projectName = project?.name || "";
        const { start, end } = getProjectDefaultDates(project);
        if (!next[pid]) {
          next[pid] = { project_id: pid, project_name: projectName, start, end, equipment_group_ids: [] };
        } else {
          next[pid] = {
            ...next[pid],
            project_id: next[pid].project_id || pid,
            project_name: next[pid].project_name || projectName,
            start: next[pid].start || start,
            end: next[pid].end || end,
            equipment_group_ids: next[pid].equipment_group_ids || [],
          };
        }
      });
      Object.keys(next).forEach((pid) => { if (!ids.includes(pid)) delete next[pid]; });
      return next;
    });
  };

  const onProjectsChange = (e) => {
    const ids = e.target.value;
    setSelectedProject(ids);
    setProjectError("");
    ensureProjectAccessKeys(ids);
  };

  const setAccessStart = (pid, date) => {
    setProjectAccess((prev) => {
      const projectName = projects.find((p) => p.project_id === pid)?.name || "";
      const current = prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null, equipment_group_ids: [] };
      let end = current.end;
      if (end && date && end < date) end = null;
      return { ...prev, [pid]: { ...current, project_id: pid, project_name: projectName, start: date, end } };
    });
  };

  const setAccessEnd = (pid, date) => {
    const projectName = projects.find((p) => p.project_id === pid)?.name || "";
    setProjectAccess((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null, equipment_group_ids: [] }), project_id: pid, project_name: projectName, equipment_group_ids: prev[pid]?.equipment_group_ids || [], end: date },
    }));
  };

  const setProjectEquipmentGroups = (pid, groupIds) => {
    const projectName = projects.find((p) => p.project_id === pid)?.name || "";
    setProjectAccess((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null }), project_id: pid, project_name: projectName, equipment_group_ids: groupIds || [] },
    }));
  };

  const scrollToFirstError = () => {
    // Wait for error state to render, then bring the first error into view.
    setTimeout(() => {
      const el = document.querySelector(".field-error, .project-error");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  const validate = () => {
    const errs = {};
    if (!formData.name?.trim()) errs.name = "Name is required.";
    if (!formData.email?.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errs.email = "Enter a valid email address.";
    if (!formData.password?.trim()) errs.password = "Password is required.";
    if (!formData.phone_number?.trim()) errs.phone = "Phone number is required.";
    else if (!/^\d{10}$/.test(formData.phone_number.trim()))
      errs.phone = "Phone number must be 10 digits.";
    // Aadhar is optional; only validate length if a value is entered.
    if (formData.adharnumber?.trim() && formData.adharnumber.trim().length !== 12)
      errs.aadhar = "Aadhar number must be 12 digits.";
    if (!formData.role) errs.role = "Role is required.";

    let hasProjectErrors = false;
    let projectValidationError = "";
    if (selectedProject.length > 0) {
      const missing = [];
      selectedProject.forEach((pid) => {
        const acc = projectAccess[pid] || {};
        if (!acc.start || !acc.end) missing.push(pid);
        else if (acc.end < acc.start) missing.push(pid);
      });
      if (missing.length) {
        projectValidationError = "Please set valid start/end dates for all selected projects.";
        setProjectError(projectValidationError);
        hasProjectErrors = true;
      } else {
        setProjectError("");
      }
    } else {
      setProjectError("");
    }

    setFieldErrors(errs);
    const firstError = Object.values(errs)[0] ?? (hasProjectErrors ? projectValidationError : null);
    if (firstError) {
      setSnackbarMessage(firstError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      scrollToFirstError();
    }
    return Object.keys(errs).length === 0 && !hasProjectErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const user_project = selectedProject.map((pid) => ({
      project_id: pid,
      access_start: formatDateApi(projectAccess[pid]?.start),
      access_end: formatDateApi(projectAccess[pid]?.end),
      equipment_group_ids: projectAccess[pid]?.equipment_group_ids || [],
    }));

    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      aadhar_number: formData.adharnumber || undefined,
      phone_number: formData.phone_number || undefined,
      role_id: formData.role,
      group_ids: formData.group,
      department_ids: formData.department_ids || [],
      user_project,
    };

    try {
      await axiosInstance.post(`${API_URL}/api/create_system_user`, payload, {
        headers: { Authorization: `Bearer ${getAuthKey()}` },
      });
      setSnackbarMessage("System user created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsDirty(false);
      setTimeout(() => navigate("/system-users"), 1500);
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || "Something went wrong.";
      const em = String(errorMsg).toLowerCase();
      if (em.includes("password")) setFieldErrors((p) => ({ ...p, password: errorMsg }));
      else if (em.includes("email")) setFieldErrors((p) => ({ ...p, email: errorMsg }));
      else if (em.includes("aadhar")) setFieldErrors((p) => ({ ...p, aadhar: errorMsg }));
      else if (em.includes("phone")) setFieldErrors((p) => ({ ...p, phone: errorMsg }));
      else { setSnackbarMessage(errorMsg); setSnackbarSeverity("error"); setSnackbarOpen(true); }
      scrollToFirstError();
      console.error("Create user failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };


  /** ---------------------- render ---------------------- */
  return (
    <div className={`user-form-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
      <div className="user-form-scroll">
        <h1 className="uf-page-title">New User Form</h1>
        <form id="user-form" onSubmit={handleSubmit} className="form-box">

            {/* ── Section 1: Personal Information ── */}
            <div className="uf-card">
              <div className="uf-section-header"><span className="uf-section-title">Personal Information</span></div>
              <div className="uf-grid">
                {/* Name */}
                <div className="uf-field">
                  <label className="uf-label">Name <span className="uf-required">*</span></label>
                  <input
                    type="text"
                    className={`uf-input${fieldErrors.name ? " uf-has-error" : ""}`}
                    placeholder="Enter Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur("name")}
                  />
                  {fieldErrors.name && <p className="uf-error">{fieldErrors.name}</p>}
                </div>

                {/* Email */}
                <div className="uf-field">
                  <label className="uf-label">Email <span className="uf-required">*</span></label>
                  <input
                    type="email"
                    className={`uf-input${fieldErrors.email ? " uf-has-error" : ""}`}
                    placeholder="Enter Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => handleBlur("email")}
                  />
                  {fieldErrors.email && <p className="uf-error">{fieldErrors.email}</p>}
                </div>

                {/* Password */}
                <div className="uf-field">
                  <label className="uf-label">Password <span className="uf-required">*</span></label>
                  <div className="uf-pw-wrap">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`uf-input${fieldErrors.password ? " uf-has-error" : ""}`}
                      placeholder="Enter Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={() => handleBlur("password")}
                    />
                    <span className="uf-pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </span>
                  </div>
                  {fieldErrors.password && <p className="uf-error">{fieldErrors.password}</p>}
                </div>

                {/* Aadhar */}
                <div className="uf-field">
                  <label className="uf-label">Aadhar Number</label>
                  <input
                    type="text"
                    className={`uf-input${fieldErrors.aadhar ? " uf-has-error" : ""}`}
                    placeholder="Enter Aadhar Number"
                    name="adharnumber"
                    value={formData.adharnumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 12);
                      setFormData((p) => ({ ...p, adharnumber: value }));
                      setFieldErrors((p) => ({ ...p, aadhar: "" }));
                    }}
                    onBlur={() => handleBlur("adharnumber")}
                  />
                  {formData.adharnumber.length > 0 && (
                    <p className="uf-counter">{formData.adharnumber.length}/12 digits</p>
                  )}
                  {fieldErrors.aadhar && <p className="uf-error">{fieldErrors.aadhar}</p>}
                </div>

                {/* Phone */}
                <div className="uf-field">
                  <label className="uf-label">Phone Number <span className="uf-required">*</span></label>
                  <input
                    type="text"
                    className={`uf-input${fieldErrors.phone ? " uf-has-error" : ""}`}
                    placeholder="Enter Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={(e) => {
                      const phoneNumber = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData((prev) => ({ ...prev, phone_number: phoneNumber }));
                      setFieldErrors((prev) => ({ ...prev, phone: "" }));
                    }}
                    onBlur={() => handleBlur("phone_number")}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                  />
                  {/* Improvement 6 — character counter for phone_number */}
                  {formData.phone_number.length > 0 && (
                    <p className="uf-counter">{formData.phone_number.length}/10 digits</p>
                  )}
                  {fieldErrors.phone && <p className="uf-error">{fieldErrors.phone}</p>}
                </div>
              </div>
            </div>

            {/* ── Section 2: Role & Access ── */}
            <div className="uf-card">
              <div className="uf-section-header"><span className="uf-section-title">Role &amp; Access</span></div>
              <div className="uf-grid">
                {/* Role */}
                <div className="uf-field">
                  <label className="uf-label">Role <span className="uf-required">*</span></label>
                  {/* Improvement 4 — loading state for Role select */}
                  <FormControl
                    fullWidth
                    size="small"
                    className={`uf-select${fieldErrors.role ? " uf-select-error" : ""}`}
                  >
                    <Select
                      value={formData.role}
                      displayEmpty
                      onChange={(e) => {
                        setFormData((p) => ({ ...p, role: e.target.value }));
                        setFieldErrors((p) => ({ ...p, role: "" }));
                      }}
                      input={<OutlinedInput />}
                      disabled={optionsLoading}
                      renderValue={(selected) => selected
                        ? roles.find((role) => role.role_id === selected)?.role_name || selected
                        : <span style={{ color: "#9ca3af" }}>Select Role</span>}
                      MenuProps={{
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            maxHeight: 260,
                            border: "1px solid #e5e8ee",
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                            "& .MuiMenuItem-root": { minHeight: 38, fontSize: "13.5px" },
                            "& .MuiMenuItem-root:hover": { backgroundColor: "#fff5f4" },
                            "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#fdebea", color: "#9f1711" },
                            "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#fbdedc" },
                          },
                        },
                      }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <MenuItem value="" disabled key="placeholder">Select Role</MenuItem>,
                          ...roles.map((role) => (
                            <MenuItem key={role.role_id} value={role.role_id}>{role.role_name}</MenuItem>
                          )),
                        ]
                      )}
                    </Select>
                  </FormControl>
                  {fieldErrors.role && <p className="uf-error">{fieldErrors.role}</p>}
                </div>

                {/* Groups */}
                <div className="uf-field">
                  <label className="uf-label">Groups</label>
                  <FormControl fullWidth size="small" className="uf-select">
                    <Select
                      multiple
                      value={formData.group}
                      displayEmpty
                      onChange={(e) => setFormData((p) => ({ ...p, group: e.target.value }))}
                      input={<OutlinedInput label="Select Group" />}
                      disabled={optionsLoading}
                      renderValue={(selected) => {
                        if (selected.length === 0) return <span style={{ color: "#aaa" }}>Select Group</span>;
                        const visible = selected.slice(0, 2);
                        const extra = selected.length - 2;
                        return (
                          <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                            {visible.map((value) => {
                              const option = groupOptions.find((opt) => opt.value === value);
                              return (
                                <Chip
                                  key={value}
                                  label={option?.label || value}
                                  size="small"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() => setFormData((p) => ({ ...p, group: p.group.filter((g) => g !== value) }))}
                                />
                              );
                            })}
                            {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 260, borderRadius: "10px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)", border: "1px solid #eaecf0",
                            "& .MuiMenuItem-root": { fontSize: "13.5px", minHeight: "36px" },
                            "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#f8fafc" },
                            "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#f1f5f9" },
                            "& .MuiMenuItem-root:hover": { backgroundColor: "#f8fafc" },
                          },
                        },
                      }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <MenuItem disabled value="" key="placeholder">Select Group</MenuItem>,
                          ...groupOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={formData.group.indexOf(option.value) > -1} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#111827" }, padding: "4px", mr: 0.5 }} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))
                        ]
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Designation */}
                <div className="uf-field">
                  <label className="uf-label">Designation</label>
                  <FormControl fullWidth size="small" className="uf-select">
                    <Select
                      multiple
                      value={formData.department_ids}
                      displayEmpty
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      onClose={() => setDeptSearch("")}
                      input={<OutlinedInput label="Select Designation" />}
                      disabled={optionsLoading}
                      renderValue={(selected) => {
                        if (selected.length === 0) return <span style={{ color: "#aaa" }}>Select Designation</span>;
                        const visible = selected.slice(0, 2);
                        const extra = selected.length - 2;
                        return (
                          <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                            {visible.map((value) => {
                              const option = departments.find((opt) => opt.id === value);
                              return (
                                <Chip
                                  key={value}
                                  label={option?.name || value}
                                  size="small"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() => setFormData((p) => ({ ...p, department_ids: p.department_ids.filter((id) => id !== value) }))}
                                />
                              );
                            })}
                            {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                            border: "1px solid #e5e8ee",
                            maxHeight: 200,
                            "& .MuiMenu-list": { py: 0.5 },
                            "& .MuiMenuItem-root": { fontSize: "13.5px", minHeight: "36px", px: 1.5 },
                            "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#fdebea" },
                            "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#fbdedc" },
                            "& .MuiMenuItem-root:hover": { backgroundColor: "#fff5f4" },
                          },
                        },
                        autoFocus: false,
                      }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <ListSubheader sx={{ p: 1, bgcolor: "background.paper" }} key="search">
                            <TextField
                              size="small"
                              fullWidth
                              autoFocus
                              placeholder="Search designation..."
                              value={deptSearch}
                              onChange={(e) => setDeptSearch(e.target.value)}
                              onKeyDown={(e) => {
                                // Stop MUI Select's type-ahead from hijacking keystrokes.
                                if (e.key !== "Escape") e.stopPropagation();
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IoSearch />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 0 3px rgba(183, 28, 28, 0.1)",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#b71c1c",
                                    borderWidth: "1px",
                                  },
                                },
                              }}
                            />
                          </ListSubheader>,
                          <MenuItem value="__all__" key="all">
                            <Checkbox checked={allFilteredSelected} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#b71c1c" }, padding: "4px", mr: 0.5 }} />
                            <ListItemText primary={allFilteredSelected ? "Deselect All" : "Select All"} />
                          </MenuItem>,
                          ...(filteredDepartments.length === 0
                            ? [<MenuItem disabled key="none">No designations found</MenuItem>]
                            : filteredDepartments.map((department) => (
                                <MenuItem key={department.id} value={department.id}>
                                  <Checkbox checked={formData.department_ids.indexOf(department.id) > -1} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#b71c1c" }, padding: "4px", mr: 0.5 }} />
                                  <ListItemText primary={department.name} />
                                </MenuItem>
                              ))
                          )
                        ]
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Projects */}
                <div className="uf-field">
                  <label className="uf-label">Select Project(s)</label>
                  <FormControl fullWidth size="small" className="uf-select">
                    <Select
                      multiple
                      value={selectedProject}
                      onChange={onProjectsChange}
                      input={<OutlinedInput label="Select a Project" />}
                      disabled={optionsLoading}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected?.length) return <span style={{ color: "#aaa" }}>Select a Project</span>;
                        const visible = selected.slice(0, 2);
                        const extra = selected.length - 2;
                        return (
                          <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                            {visible.map((id) => {
                              const proj = projects.find((p) => p.project_id === id);
                              return <Chip key={id} label={proj?.name || id} size="small" />;
                            })}
                            {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 260, borderRadius: "10px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)", border: "1px solid #eaecf0",
                            "& .MuiMenuItem-root": { fontSize: "13.5px", minHeight: "36px" },
                            "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#f8fafc" },
                            "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#f1f5f9" },
                            "& .MuiMenuItem-root:hover": { backgroundColor: "#f8fafc" },
                          },
                        },
                      }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <MenuItem disabled value="" key="placeholder">Select a Project</MenuItem>,
                          ...projects.map((project) => (
                            <MenuItem key={project.project_id} value={project.project_id}>
                              <Checkbox checked={selectedProject.includes(project.project_id)} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#111827" }, padding: "4px", mr: 0.5 }} />
                              {project.name}
                            </MenuItem>
                          ))
                        ]
                      )}
                    </Select>
                  </FormControl>
                  {projectError && <p className="uf-error" style={{ marginTop: 6 }}>{projectError}</p>}
                </div>
              </div>
            </div>

            {/* ── Section 3: Project Configurations (conditional) ── */}
            {selectedProject.length > 0 && (
              <div className="uf-card" ref={projectConfigRef}>
                <div className="uf-section-header"><span className="uf-section-title">Project Configurations</span></div>
                <div>
                  {selectedProject.map((pid) => {
                    const proj = projects.find((p) => p.project_id === pid);
                    const defaultDates = getProjectDefaultDates(proj);
                    const access = projectAccess[pid] || { project_id: pid, project_name: proj?.name || "", ...defaultDates, equipment_group_ids: [] };
                    const eqSearch = equipGroupSearch[pid] || "";
                    const filteredEqGroups = eqSearch.trim()
                      ? equipmentGroupOptions.filter((o) => o.label.toLowerCase().includes(eqSearch.trim().toLowerCase()))
                      : equipmentGroupOptions;
                    const allEqGroupsSelected =
                      filteredEqGroups.length > 0 &&
                      filteredEqGroups.every((o) => (access.equipment_group_ids || []).includes(o.value));
                    const isCollapsed = !!collapsedProjects[pid];

                    return (
                      <div className="uf-project-card" key={pid}>
                        <div className="uf-project-header">
                          <span className="uf-project-name">{proj?.name || pid}</span>
                            <button
                              type="button"
                              className="uf-project-toggle"
                              onClick={() => toggleProject(pid)}
                              aria-expanded={!isCollapsed}
                            >
                              <IoChevronDown
                                className={`uf-project-toggle-icon${isCollapsed ? " is-collapsed" : ""}`}
                                aria-hidden="true"
                              />
                              <span>{isCollapsed ? "Show" : "Hide"}</span>
                            </button>
                          </div>
                          <div className="uf-project-body" style={{ display: isCollapsed ? "none" : undefined }}>
                            <section className="uf-project-subsection">
                              <div className="uf-project-subsection-title">
                                <IoCalendarOutline aria-hidden="true" />
                                <span>Timeline</span>
                              </div>
                              <div className="row uf-project-date-row">
                              <div className="col-6">
                                <label className="form-label small">Start date</label>
                                <DatePicker
                                  selected={access.start}
                                  onChange={(d) => setAccessStart(pid, d)}
                                  dateFormat="dd-MM-yyyy"
                                  className="form-control form-control-sm"
                                  placeholderText="dd-mm-yyyy"
                                  maxDate={access.end || undefined}
                                  isClearable
                                />
                              </div>
                              <div className="col-6">
                                <label className="form-label small">End date</label>
                                <DatePicker
                                  selected={access.end}
                                  onChange={(d) => setAccessEnd(pid, d)}
                                  dateFormat="dd-MM-yyyy"
                                  className="form-control form-control-sm"
                                  placeholderText="dd-mm-yyyy"
                                  minDate={access.start || undefined}
                                  isClearable
                                />
                              </div>
                              </div>
                              <div className="uf-project-duration">
                                Duration: {getDurationDays(access.start, access.end) ?? "—"} days
                              </div>
                            </section>

                            <section className="uf-project-subsection">
                              <div className="uf-project-subsection-title">
                                <IoPricetagsOutline aria-hidden="true" />
                                <span>Equipment Groups</span>
                              </div>
                              <div>
                                <FormControl fullWidth size="small" className="uf-select">
                                  <Select
                                    multiple
                                    value={access.equipment_group_ids || []}
                                    displayEmpty
                                    onClose={() => setEquipGroupSearch((prev) => ({ ...prev, [pid]: "" }))}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value.includes("__all__")) {
                                        const filteredIds = filteredEqGroups.map((o) => o.value);
                                        const current = access.equipment_group_ids || [];
                                        setProjectEquipmentGroups(
                                          pid,
                                          allEqGroupsSelected
                                            ? current.filter((id) => !filteredIds.includes(id))
                                            : Array.from(new Set([...current, ...filteredIds])),
                                        );
                                        return;
                                      }
                                      setProjectEquipmentGroups(pid, value);
                                    }}
                                    input={<OutlinedInput label="Select equipment groups..." />}
                                    renderValue={(selected) => {
                                      if (selected.length === 0) return <span className="uf-project-placeholder">Select equipment groups...</span>;
                                      const visible = selected.slice(0, 2);
                                      const extra = selected.length - 2;
                                      return (
                                        <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                                          {visible.map((value) => {
                                            const option = equipmentGroupOptions.find((opt) => opt.value === value);
                                            return (
                                              <Chip
                                                key={value}
                                                label={option?.label || value}
                                                size="small"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onDelete={() => setProjectEquipmentGroups(pid, (access.equipment_group_ids || []).filter((id) => id !== value))}
                                              />
                                            );
                                          })}
                                          {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                                        </Box>
                                      );
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          maxHeight: 300, borderRadius: "10px",
                                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)", border: "1px solid #eaecf0",
                                          "& .MuiMenuItem-root": { fontSize: "13.5px", minHeight: "36px" },
                                          "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#f8fafc" },
                                          "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#f1f5f9" },
                                          "& .MuiMenuItem-root:hover": { backgroundColor: "#f8fafc" },
                                        },
                                      },
                                      autoFocus: false,
                                    }}
                                  >
                                    <ListSubheader sx={{ p: 1, bgcolor: "background.paper" }}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        autoFocus
                                        placeholder="Search equipment groups..."
                                        value={eqSearch}
                                        onChange={(e) => setEquipGroupSearch((prev) => ({ ...prev, [pid]: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key !== "Escape") e.stopPropagation(); }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <IoSearch />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </ListSubheader>
                                    <MenuItem value="__all__">
                                      <Checkbox checked={allEqGroupsSelected} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#111827" }, padding: "4px", mr: 0.5 }} />
                                      <ListItemText primary={allEqGroupsSelected ? "Deselect All" : "Select All"} />
                                    </MenuItem>
                                    {filteredEqGroups.length === 0 ? (
                                      <MenuItem disabled>No equipment groups found</MenuItem>
                                    ) : (
                                      filteredEqGroups.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          <Checkbox checked={(access.equipment_group_ids || []).indexOf(option.value) > -1} sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#111827" }, padding: "4px", mr: 0.5 }} />
                                          <ListItemText primary={option.label} />
                                        </MenuItem>
                                      ))
                                    )}
                                  </Select>
                                </FormControl>
                              </div>
                            </section>

                          </div>
                        </div>
                    );
                  })}
                </div>
              </div>
            )}

        </form>
      </div>

      <div className="user-form-footer">
        <button type="button" className="uf-btn uf-btn-cancel" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" form="user-form" className="uf-btn uf-btn-save" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="outlined"
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            color: snackbarSeverity === "success" ? "#2e7d32" : "#d32f2f",
            borderColor: snackbarSeverity === "success" ? "#2e7d32" : "#d32f2f",
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
